import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { InputSummary, InputSummaryItem } from '../../input-summary';
import { EmissionRef } from '../ghg-emission/emission.model';
import { UnitValue } from '../../data-structures/unit-value.model';
import {v4 as uuid} from 'uuid';
import AnessaListItemUtil, { AnessaListItem } from '../../anessa-list-item.interface';


export interface FuelInterface {
  _id: string;
  name: string;
  uuid: string;
  typeName: string;
  type: FuelType;
  description: string;
  quantity: UnitValue;
  perType: string;
  period: UnitValue;
  usageIncrease: UnitValue;
  cost: UnitValue;
  amount: UnitValue;
  cpi: UnitValue;
  emission: EmissionRef;
}

export class Fuel implements AnessaListItem{
  _id: string;
  name: string;
  uuid: string;
  // this is the name of an emission data
  typeName: string;
  typeID: string;
  type: FuelType | null;
  description: string;
  quantity: UnitValue;
  period: UnitValue;
  usageIncrease: UnitValue;
  cost: UnitValue;
  amount: UnitValue;
  cpi: UnitValue;
  emission: EmissionRef;
  perType: string;

  constructor(param: FuelInterface = {
    _id: null,
    name: '',
    uuid: null,
    typeName: '',
    type: null,
    description: '',
    quantity: new UnitValue(),
    perType: PeriodType.PERIOD,
    period: new UnitValue(),
    usageIncrease: new UnitValue,
    cost: new UnitValue(),
    amount: new UnitValue,
    cpi: new UnitValue(),
    emission: new EmissionRef(),
  }) {
    if (param._id) {
      this._id = param._id;
    } else {
      delete this._id;
    }
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.typeName = param.typeName;
    this.type = param.type;
    this.description = param.description;
    this.quantity = new UnitValue(param.quantity);
    if(param.perType){
      this.perType = param.perType
    }
    else {
      this.perType = PeriodType.PERIOD;
    }
    //param.perType ? this.perType = param.perType : this.perType = PeriodType.PERIOD;
    this.period = new UnitValue(param.period);
    this.usageIncrease = new UnitValue(param.usageIncrease);
    this.cost = new UnitValue(param.cost);
    this.amount = new UnitValue(param.amount);
    this.cpi = new UnitValue(param.cpi);
    this.emission = new EmissionRef(param.emission);
  }

  static deepCopy(fuel: Fuel): Fuel {
    let copy;
    if (fuel) {
      copy = new Fuel(fuel);
      return copy;
    }
    throw new Error('Fuel copy failed');
  }

  static getFuelTypeForValue(value: string): FuelType {
    const keys = Object.keys(FuelType).filter(x => FuelType[x] === value);
    if (keys.length > 0) {
      return FuelType[keys[0]];
    } else {
      throw Error('invalid value provided for FuelType enum');
    }
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  getFinancialInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getFinancialSummaryInputs(path);
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.description, this.description, [...path, 'description']);
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.quantity, this.quantity, [...path, 'quantity']);
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.period, this.period, [...path, 'period']);
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.usageIncrease, this.usageIncrease, [...path, 'usageIncrease']);
    return inputs;
  }

  getFinancialSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.cost, this.cost, [...path, 'cost']);
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.amount, this.amount, [...path, 'amount']);
    inputs = InputSummaryUtils.performPush(inputs, FuelPropName.cpi, this.cpi, [...path, 'cpi']);
    return inputs;
  }

  getUuidForPath(): string {
    return AnessaListItemUtil.getUuidForPath(this.uuid);
  }
}

export enum FuelPropName {
  name = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.NAME',
  description = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.DESCRIPTION',
  type = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.TYPE',
  quantity = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.QUANTITY',
  period = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.PERIOD',
  usageIncrease = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.USAGE_INCREASE',
  cost = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.COST',
  amount = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.AMOUNT',
  cpi = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.CPI',
  emission = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.EMISSION',
  lcfsRin = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.LCFSRIN',
  period_typ= 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.FUEL.PERTYPE',
  id_name = 'name',
  id_description = 'description',
  id_type = 'type',
  id_quantity = 'quantity',
  id_period = 'period',
  id_usageIncrease = 'usage increase',
  id_cost = 'cost',
  id_amount = 'amount',
  id_cpi = 'CPI',
  id_emission = 'emission',
  id_lcfsRin = 'LCFS RIN',
}

export enum FuelType {
  Electric = 'electric',
  Heat = 'heat',
  Biometh = 'biometh',
  Biofert = 'biofert',
  Digestate = 'digestate',
  Solid = 'solid',
  Wood = 'wood',
  Agriculture = 'agri',
  Liquid = 'liquid',
  Gas = 'gas',
  NaturalGas = 'natgas',
  Other = 'other',
}

// values should match the ConnectionType enum in java
export enum PeriodType {
  PERIOD = 'period',
  FEEDAMOUNT = 'feedstock amount',
  ELECTRIC = 'electric',
  HEAT = 'heat',
  BIOGAS = 'biogas',
  BIOMETHANE = 'biomethane',
  WHOLE = 'whole digestate',
  SOLID = 'solid digestate',
  THIN = 'thin digestate',
  DEFAULT = 'please select'
}
